import { useState, useEffect } from 'react'
import { Customer, User } from '../../services/api'
import api from '../../services/api'
import { LoadingSpinner, ErrorAlert, Badge } from '../common'
import { ApiError } from '../../services/api/errors/ApiError'

interface UserManagementProps {
  customer: Customer
}

interface Role {
  id: string
  name: string
  description?: string
  permissions: string[]
}

interface InviteUserModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    given_name: '',
    surname: '',
    job_title: '',
    role_name: 'CampaignManager' as 'CustomerAdmin' | 'CampaignManager'
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      await api.invitations.create({
        email: formData.email,
        given_name: formData.given_name,
        surname: formData.surname,
        job_title: formData.job_title,
        role_name: formData.role_name
      })

      // Reset form
      setFormData({
        email: '',
        given_name: '',
        surname: '',
        job_title: '',
        role_name: 'CampaignManager'
      })

      setSuccess(true)
      setTimeout(() => {
        onSuccess()
        onClose()
        setSuccess(false)
      }, 1500)
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.getUserMessage())
      } else {
        setError('Failed to create user')
      }
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Invite New User
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>

        {error && (
          <div className="mb-4">
            <ErrorAlert message={error} />
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded">
            <p className="text-sm text-green-800 dark:text-green-200">
              ✓ Invitation sent successfully!
            </p>
          </div>
        )}

        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            The user will receive an email with a link to accept the invitation and set their password.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email Address <span className="text-gray-400">*</span>
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md 
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-blue-500 focus:border-blue-500"
                placeholder="user@example.com"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  First Name <span className="text-gray-400">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.given_name}
                  onChange={(e) => setFormData({ ...formData, given_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md 
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                           focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Last Name <span className="text-gray-400">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.surname}
                  onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md 
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                           focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Job Title (Optional)
              </label>
              <input
                type="text"
                value={formData.job_title}
                onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md 
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., IT Manager"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Role <span className="text-gray-400">*</span>
              </label>
              <select
                required
                value={formData.role_name}
                onChange={(e) => setFormData({ ...formData, role_name: e.target.value as 'CustomerAdmin' | 'CampaignManager' })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md 
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="CampaignManager">Campaign Manager</option>
                <option value="CustomerAdmin">Account Administrator</option>
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {formData.role_name === 'CustomerAdmin' 
                  ? 'Full access to all features and settings'
                  : 'Can create and manage campaigns'}
              </p>
            </div>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
            <span className="text-gray-400">*</span> Required fields
          </p>

          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md 
                       text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700
                       disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
            type="submit"
            disabled={loading || success}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 
            dark:hover:bg-blue-600 text-white rounded-md disabled:opacity-50 
            transition-colors flex items-center justify-center gap-2"
            >
            {loading ? (
            <>
            <LoadingSpinner size="sm" />
            <span>Sending...</span>
            </>
            ) : success ? (
            '✓ Sent!'
            ) : (
                'Send Invitation'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const UserManagement: React.FC<UserManagementProps> = ({ customer }) => {
  const [users, setUsers] = useState<User[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null)

  const loadUsers = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const users = await api.users.list({})
      setUsers(users)
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.getUserMessage())
      } else {
        setError('Failed to load users')
      }
    } finally {
      setLoading(false)
    }
  }

  const loadRoles = async () => {
    try {
      // Mock roles for now - implement getRoles endpoint later if needed
      setRoles([
        { id: 'admin', name: 'Administrator', permissions: ['all'] },
        { id: 'manager', name: 'Campaign Manager', permissions: ['campaigns:create', 'campaigns:read'] },
        { id: 'viewer', name: 'Viewer', permissions: ['campaigns:read'] }
      ])
    } catch (err) {
      console.error('Failed to load roles:', err)
    }
  }

  useEffect(() => {
    loadUsers()
    loadRoles()
  }, [])

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return
    }

    try {
      setDeletingUserId(userId)
      await api.users.delete(userId)
      await loadUsers()
    } catch (err) {
      if (err instanceof ApiError) {
        alert(err.getUserMessage())
      } else {
        alert('Failed to delete user')
      }
    } finally {
      setDeletingUserId(null)
    }
  }

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { variant: 'default' | 'success' | 'warning' | 'error' | 'info'; label: string }> = {
      active: { variant: 'success', label: 'Active' },
      pending_invitation: { variant: 'warning', label: 'Pending Signup' },
      invited: { variant: 'warning', label: 'Pending Signup' },
      inactive: { variant: 'default', label: 'Inactive' },
      suspended: { variant: 'error', label: 'Suspended' }
    }

    const config = statusMap[status] || { variant: 'default' as const, label: status }

    return (
      <Badge variant={config.variant}>
        {config.label}
      </Badge>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">Users & Permissions</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage team members and their access levels
          </p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 
                   text-white rounded-md transition-colors"
        >
          Invite User
        </button>
      </div>

      {error && (
        <div className="mb-4">
          <ErrorAlert message={error} />
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        {users.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">No users yet</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-4 text-blue-600 hover:text-blue-700 dark:text-blue-400"
            >
              Invite your first user
            </button>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {users.map((user) => (
                <tr key={user.user_id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {user.display_name || `${user.given_name} ${user.surname}`}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full 
                                   bg-purple-100 dark:bg-purple-900 
                                   text-purple-800 dark:text-purple-200">
                      {user.role_name || 'User'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(user.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {user.last_login 
                      ? new Date(user.last_login).toLocaleDateString() 
                      : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleDeleteUser(user.user_id)}
                      disabled={deletingUserId === user.user_id}
                      className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 
                               disabled:opacity-50 transition-colors"
                    >
                      {deletingUserId === user.user_id ? 'Deleting...' : 'Delete'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <InviteUserModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={loadUsers}
      />
    </div>
  )
}

export default UserManagement
